import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, vShow as _vShow, withDirectives as _withDirectives, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "calendar-month-year-text-container black medium" }
const _hoisted_2 = { class: "calendar-month-year-text-animation" }
const _hoisted_3 = { class: "calendar-month-year-text" }

import {
  computed,
  ComputedRef,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useStore } from 'vuex';
import { isSameMonth } from 'date-fns';
import { Lang } from '@/types';
import Emitter, { EVENT_NAMES } from '@/plugins/emitter';
import WeekText from './WeekText.vue';
import CalendarMonth from './CalendarMonth.vue';
import PrevMonthButton from './PrevMonthButton.vue';
import NextMonthButton from './NextMonthButton.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'Calendar',
  props: {
  dates: {
    type: Array<Date>,
    default: [],
  },
  minDate: {
    type: Date,
    required: true,
  },
  maxDate: {
    type: Date,
    required: true,
  },
  firstDayOfWeek: {
    type: Number,
    default: 0,
  },
},
  setup(__props) {

const props = __props

const store = useStore();



const calendarRef = ref<HTMLDivElement>();
const monthRef = ref<Array<HTMLDivElement>>();
const displayMonthsDate = ref<Date[]>([]);

const isMobile: ComputedRef<boolean> = computed(
  () => store.state.support.isMobile
);
const lang: ComputedRef<Lang.LangItem> = computed(
  () => store.state.support.lang
);
const monthsCount: ComputedRef<number> = computed(() =>
  isMobile.value ? 13 : 2
);
const isPrevMonthAvailable = computed(
  () =>
    !(
      displayMonthsDate.value[0] &&
      isSameMonth(displayMonthsDate.value[0], new Date(props.minDate))
    )
);
const isNextMonthAvailable = computed(
  () =>
    props.maxDate == null ||
    !(
      displayMonthsDate.value[monthsCount.value - 1] &&
      isSameMonth(
        displayMonthsDate.value[monthsCount.value - 1],
        new Date(props.maxDate)
      )
    )
);

function showMonthYear(date: Date) {
  return date.toLocaleString(lang.value.locale, {
    month: 'long',
    year: 'numeric',
  });
}

function getDisplayMonths(count) {
  const today = new Date();
  const lastDateInArray = props.dates[props.dates.length - 1];
  const selectedDate = lastDateInArray ? lastDateInArray : today;
  let array: Date[] = [];
  if (count === 1) {
    array.push(selectedDate);
  } else if (count === 2) {
    for (let i = 0; i < count; i++) {
      let date = !array[i - 1]
        ? new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 15)
        : new Date(
            new Date(array[i - 1]).getFullYear(),
            new Date(array[i - 1]).getMonth() + 1,
            15
          );
      array.push(date);
    }
  } else {
    for (let i = 0; i < count; i++) {
      let date = !array[i - 1]
        ? new Date(today.getFullYear(), today.getMonth(), 15)
        : new Date(
            new Date(array[i - 1]).getFullYear(),
            new Date(array[i - 1]).getMonth() + 1,
            15
          );
      array.push(date);
    }
  }
  displayMonthsDate.value = array;
}

function prevMonth() {
  let months = [];
  displayMonthsDate.value.forEach((element) => {
    let item = new Date(
      new Date(element).getFullYear(),
      new Date(element).getMonth() - 1,
      15
    );
    months.push(item);
  });
  displayMonthsDate.value = months;
}

function nextMonth() {
  let months = [];
  displayMonthsDate.value.forEach((element) => {
    let item = new Date(
      new Date(element).getFullYear(),
      new Date(element).getMonth() + 1,
      15
    );
    months.push(item);
  });
  displayMonthsDate.value = months;
}

function scrollWithAnimate(to: number) {
  if (!calendarRef.value) {
    return;
  }

  const start = calendarRef.value?.scrollTop,
    change = to - start,
    startDate = +new Date();

  function animateScroll() {
    if (!calendarRef.value) {
      return;
    }

    const currentDate = +new Date();
    const currentTime = currentDate - startDate;
    calendarRef.value.scrollTop = parseInt(
      easeInOutQuad(currentTime, start, change, 250)
    );
    if (currentTime < 250) requestAnimationFrame(animateScroll);
    else calendarRef.value.scrollTop = to;
  }

  animateScroll();

  function easeInOutQuad(t, b, c, d) {
    t /= d / 2;
    if (t < 1) return (c / 2) * t * t + b;
    t--;
    return (-c / 2) * (t * (t - 2) - 1) + b;
  }
}

function onTransitionEnd() {
  const today = new Date();
  const lastDateInArray = props.dates[props.dates.length - 1];
  const selectedDate = lastDateInArray ? lastDateInArray : today;
  if (isMobile.value && selectedDate) {
    const checkDate = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      15
    );
    const index = displayMonthsDate.value.indexOf(
      displayMonthsDate.value.find(
        (x) => String(x).toLowerCase() === String(checkDate).toLowerCase()
      )
    );

    if (monthRef.value[index] && monthRef.value[index]?.offsetTop) {
      scrollWithAnimate(monthRef.value[index].offsetTop - 140);
    }
  }
}

watch(isMobile, () => {
  getDisplayMonths(monthsCount.value);
});

onMounted(() => {
  getDisplayMonths(monthsCount.value);
  Emitter.$on(EVENT_NAMES.CALENDAR_TRANSITION_END, onTransitionEnd);
});

onBeforeUnmount(() => {
  Emitter.$off(EVENT_NAMES.CALENDAR_TRANSITION_END, onTransitionEnd);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "calendarRef",
    ref: calendarRef,
    class: "calendar-month-root flex between-xs"
  }, [
    (!_unref(isMobile))
      ? _withDirectives((_openBlock(), _createBlock(PrevMonthButton, {
          key: 0,
          class: "calendar-month-select-left",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (prevMonth()))
        }, null, 512)), [
          [_vShow, _unref(isPrevMonthAvailable)]
        ])
      : _createCommentVNode("", true),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(displayMonthsDate.value, (month, i) => {
      return (_openBlock(), _createElementBlock("div", {
        ref_for: true,
        ref_key: "monthRef",
        ref: monthRef,
        class: "calendar-month-wrapper",
        key: i
      }, [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, _toDisplayString(showMonthYear(month)), 1)
          ])
        ]),
        (!_unref(isMobile))
          ? (_openBlock(), _createBlock(WeekText, {
              key: 0,
              firstDayOfWeek: __props.firstDayOfWeek
            }, null, 8, ["firstDayOfWeek"]))
          : _createCommentVNode("", true),
        _createVNode(CalendarMonth, {
          minDate: __props.minDate,
          maxDate: __props.maxDate,
          displayDate: month,
          firstDayOfWeek: __props.firstDayOfWeek,
          dates: __props.dates
        }, null, 8, ["minDate", "maxDate", "displayDate", "firstDayOfWeek", "dates"])
      ]))
    }), 128)),
    (!_unref(isMobile))
      ? _withDirectives((_openBlock(), _createBlock(NextMonthButton, {
          key: 1,
          onClick: _cache[1] || (_cache[1] = ($event: any) => (nextMonth())),
          class: "calendar-month-select-right"
        }, null, 512)), [
          [_vShow, _unref(isNextMonthAvailable)]
        ])
      : _createCommentVNode("", true)
  ], 512))
}
}

})